import { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FileImage, Eye, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const ApplicantDocuments = ({ borrowerId }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState(null);
  const [url, setUrl] = useState(null);
  const [viewError, setViewError] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let active = true;
    api.get(`/borrowers/${borrowerId}/documents`).then(({ data }) => { if (active) setDocs(data); })
      .catch(() => { if (active) setError(true); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [borrowerId]);
  useEffect(() => {
    if (!selected) return;
    let active = true, objectUrl;
    setUrl(null); setViewError(false);
    api.get(`/documents/${selected.id}/download`, { responseType: "blob" }).then(({ data }) => {
      if (active) { objectUrl = URL.createObjectURL(data); setUrl(objectUrl); }
    }).catch(() => { if (active) setViewError(true); });
    return () => { active = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [selected]);
  const review = async (status) => {
    setBusy(true);
    try {
      await api.patch(`/documents/${selected.id}`, null, { params: { review_status: status } });
      setDocs((prev) => prev.map((d) => d.id === selected.id ? { ...d, review_status: status } : d));
      setSelected(null); toast.success(`Document ${status.toLowerCase()}`);
    } catch { toast.error("Could not update document review"); }
    finally { setBusy(false); }
  };
  return (
    <section className="rounded-xl border border-white/10 bg-[#111826] p-5 space-y-4" data-testid="applicant-documents">
      <div><h2 className="font-display text-lg text-white font-semibold">ID photos & supporting documents</h2><p className="text-xs text-slate-400 mt-1">Images stay hidden until you open a document for review.</p></div>
      {loading ? <p data-testid="documents-loading" className="text-sm text-slate-400">Loading documents…</p>
        : error ? <p data-testid="documents-error" role="alert" className="text-sm text-rose-300">Could not load documents. Reload the record to retry.</p>
        : !docs.length ? <div data-testid="documents-empty" className="rounded-lg border border-dashed border-white/10 p-6 text-center text-sm text-slate-400">No documents submitted yet. ID front, ID back and selfie can be uploaded in the borrower portal.</div>
        : <div className="grid sm:grid-cols-2 gap-3">{docs.map((doc) => (
          <div key={doc.id} data-testid={`checking-document-${doc.id}`} className="rounded-lg border border-white/10 bg-[#0D1420] p-4 min-w-0">
            <div className="flex gap-3 items-start"><FileImage className="h-8 w-8 text-sky-400 shrink-0" /><div className="min-w-0"><p className="capitalize text-sm font-semibold text-white">{doc.kind.replace(/_/g, " ")}</p><p className="text-xs text-slate-500 truncate">{doc.original_filename}</p></div></div>
            <div className="flex items-center justify-between gap-2 mt-4"><span data-testid={`checking-document-status-${doc.id}`} className="text-xs text-slate-400">{doc.review_status} · {fmtDate(doc.created_at)}</span><Button size="sm" variant="secondary" onClick={() => setSelected(doc)} data-testid={`checking-document-view-${doc.id}`}><Eye className="h-3.5 w-3.5 mr-1" /> View</Button></div>
          </div>
        ))}</div>}
      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent data-testid="private-document-viewer" className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[#101624] text-white border-white/10">
          <DialogHeader><DialogTitle>Private document review</DialogTitle><DialogDescription>{selected?.original_filename} · Authorized verification only</DialogDescription></DialogHeader>
          {viewError ? <p role="alert" data-testid="document-preview-error" className="text-rose-300">Unable to retrieve this document. Close and try again.</p>
            : !url ? <div data-testid="document-preview-loading" className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
            : selected?.content_type === "application/pdf" ? <iframe data-testid="document-pdf-preview" title="Applicant document" src={url} className="w-full h-[50vh] bg-white" />
            : <img data-testid="document-image-preview" src={url} alt={`${selected?.kind.replace(/_/g, " ")} submitted for verification`} className="w-full max-h-[50vh] object-contain" />}
          <div className="flex flex-wrap items-center gap-2"><ShieldCheck className="h-4 w-4 text-sky-400" /><span className="text-xs text-slate-400 mr-auto">Check validity and consistency.</span>
            <Button data-testid="checking-document-approve" onClick={() => review("Approved")} disabled={busy || !url} className="bg-emerald-600 hover:bg-emerald-500">Approve document</Button>
            <Button data-testid="checking-document-reject" variant="secondary" onClick={() => review("Rejected")} disabled={busy || !url}>Reject document</Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};
